import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Replacement for `window.prompt()` — a CadencEZ-styled single-field input
 * dialog. Submits on Enter, cancels on Escape.
 */
export function PromptModal({
  open,
  title,
  label,
  placeholder,
  defaultValue,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  /** Optional secondary line under the title. */
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  /** Called with the entered value. Modal closes via `onCancel` after — caller controls. */
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset draft + autofocus when re-opened.
  useEffect(() => {
    if (open) {
      setValue(defaultValue ?? '');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, defaultValue]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={onCancel}
        className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]"
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = value.trim();
          if (trimmed) onConfirm(trimmed);
        }}
        className="relative w-full max-w-md rounded-md border border-border bg-card shadow-xl"
      >
        <div className="px-6 pt-5 pb-4 space-y-3">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {label && (
            <label className="block text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
              {label}
            </label>
          )}
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 outline-none"
          />
        </div>
        <div className="flex items-center justify-end gap-2 px-6 pb-5">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 px-3.5 rounded-md border border-border text-[12.5px] font-semibold text-foreground hover:bg-foreground/[0.04]"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!value.trim()}
            className="h-9 px-3.5 rounded-md bg-foreground text-background text-[12.5px] font-semibold hover:bg-foreground/90 disabled:opacity-60"
          >
            {confirmLabel ?? t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
